import { useMemo } from "react";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { twMerge } from "tailwind-merge";
import { cn } from "utils/cn";
import { IssuerLogo } from "views/issuerDetails/components/IssuerLogo";
import { LogoOption } from "./types";

interface IssuerSelectProps {
  onSelect: (ids: string[]) => void;
  selected: string[];
  issuers: LogoOption[];
  /**
   * Whether the CMS gave us any issuers at all, ignoring the current filter.
   * Distinguishes "the CMS returned nothing" from "your filter matched none of
   * what it returned" — the same empty grid, different cause.
   */
  hasIssuers: boolean;
  /**
   * Still waiting on a source the list depends on. Shows placeholders instead
   * of a partial list, which would otherwise fill in tile by tile — or flash
   * "no matches" — as each source arrives.
   */
  loading?: boolean;
}

/**
 * The fund-company picker.
 *
 * Every fund company we know of is shown, including the ones we have no email
 * address for: those are greyed out and unselectable, below the contactable
 * ones. Hiding them made the portal look like it only knew about a handful of
 * companies; showing them says what is actually true — we know them, we just
 * cannot reach them yet.
 *
 * Built as a grid of logo tiles rather than a list or a combobox because the
 * logo is the fastest way to find a fund company: people recognise Lannebo's
 * mark before they read its name. The grid grows to fit — the modal has its
 * own scroll, and a picker capped at some inner height would scroll inside a
 * scroll.
 */
export const IssuerSelect = ({
  issuers = [],
  selected,
  onSelect,
  hasIssuers,
  loading = false,
}: IssuerSelectProps) => {
  const { t } = useModifiedTranslation();

  const [contactable, unreachable] = useMemo(
    () => [
      issuers.filter((issuer) => issuer.canBeContacted),
      issuers.filter((issuer) => !issuer.canBeContacted),
    ],
    [issuers]
  );

  const handleClick = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter((selectedId) => selectedId !== id)
      : [...selected, id];

    onSelect(newSelected);
  };

  const allSelected =
    contactable.length > 0 &&
    contactable.every(({ id }) => selected.includes(id));

  /** Selecting everything is a choice among the options, not a control above
   * them, so it sits in the grid as the first tile and toggles like any other:
   * on when all of them are on, off on a second click. */
  const toggleAll = () =>
    onSelect(allSelected ? [] : contactable.map(({ id }) => id));

  if (loading) {
    return (
      <div role="status" aria-busy="true" className="grid grid-cols-3 gap-2">
        <span className="sr-only">
          {t("contactModal.issuerSelect.loading")}
        </span>
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="h-10 rounded border animate-pulse border-primary-10 bg-primary-5"
          />
        ))}
      </div>
    );
  }

  if (issuers.length === 0) {
    return (
      <div className="w-full text-center">
        <span className="text-sm text-primary-40">
          {hasIssuers
            ? t("contactModal.issuerSelect.noneMatchFilter")
            : t("contactModal.issuerSelect.noneAvailable")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {contactable.length > 0 ? (
        // Unheaded on purpose: these tiles look pickable and are pickable, so
        // a label over them states the obvious. Only the exception below needs
        // to announce itself.
        <Group>
          {/* Pointless with a single option, where it would just be that
              option under another name. */}
          {contactable.length > 1 && (
            <Tile
              name={t("contactModal.issuerSelect.all")}
              isSelected={allSelected}
              onClick={toggleAll}
              emphasis
            />
          )}
          {contactable.map((issuer) => (
            <Tile
              key={issuer.id}
              name={issuer.name}
              logoUrl={issuer.logoUrl}
              isSelected={selected.includes(issuer.id)}
              onClick={() => handleClick(issuer.id)}
            />
          ))}
        </Group>
      ) : (
        // The grid is full but nothing in it can be mailed. Without this the
        // user is left staring at a wall of grey wondering what they did wrong.
        <span className="text-sm text-primary-40">
          {t("contactModal.issuerSelect.noneSelectable")}
        </span>
      )}

      {unreachable.length > 0 && (
        <Group
          heading={t("contactModal.issuerSelect.notContactable")}
          amount={unreachable.length}
        >
          {unreachable.map((issuer) => (
            <Tile
              key={issuer.id}
              name={issuer.name}
              logoUrl={issuer.logoUrl}
              isSelected={false}
              hint={t("contactModal.issuerSelect.notContactableHint", {
                name: issuer.name,
              })}
            />
          ))}
        </Group>
      )}
    </div>
  );
};

/**
 * A group of tiles, headed only where the heading earns its place. It is in
 * the same sentence case as every other label in the modal, and the count sits
 * in its own muted span rather than inside the translated string: it is a
 * number, not part of the sentence, and it should not compete with the words
 * for attention.
 */
const Group = ({
  heading,
  amount,
  children,
}: {
  heading?: string;
  amount?: number;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    {heading && (
      <div className="flex gap-1.5 items-baseline">
        <span className="text-sm font-medium text-primary-100">{heading}</span>
        <span className="text-sm font-normal text-primary-40">{amount}</span>
      </div>
    )}
    <div className="grid grid-cols-3 gap-2">{children}</div>
  </div>
);

interface TileProps {
  name: string;
  logoUrl?: string;
  isSelected: boolean;
  /** Present only on a tile that cannot be picked; also disables it. */
  hint?: string;
  /** The "all" tile, which has no logo and carries the row on its own. */
  emphasis?: boolean;
  onClick?: () => void;
}

const Tile = ({
  name,
  logoUrl,
  isSelected,
  hint,
  emphasis,
  onClick,
}: TileProps) => (
  <button
    type="button"
    disabled={!!hint}
    // Some browsers drop the tooltip on a disabled button, so this is a second
    // line of explanation — the group heading above is the first.
    title={hint}
    onClick={(e) => {
      e.preventDefault();
      onClick?.();
    }}
    className={twMerge(
      "min-h-10 py-1 border flex-row rounded bg-white-100 border-primary-20 flex items-center justify-start transition duration-150 ease-out",
      isSelected && "bg-secondary-5 border-secondary-100",
      // Written as a plain conditional rather than an `enabled:` variant: the
      // Tailwind build here is CRA's own 3.0.7, and a variant it does not know
      // compiles to nothing at all — silently, with no build error.
      //
      // Nothing moves on hover. A grid of 43 tiles that shift under the cursor
      // reads as instability, not as feedback. The border, the tint and the
      // shadow say "this one" without the row jittering; the tint is lighter
      // than the selected one so a hovered tile never impersonates a picked
      // one, and `active` deepens the shadow so a click still feels pressed.
      !hint &&
        "cursor-pointer hover:border-secondary-100 hover:bg-secondary-2 hover:shadow-md active:shadow-sm",
      // Keyboard users get the same affordance as the mouse, which a hover
      // state alone leaves them out of.
      !hint &&
        "focus-visible:outline-none focus-visible:border-secondary-100 focus-visible:shadow-md",
      // No logo to hold the left edge, so the label centres instead of
      // floating in the space a missing mark would have taken.
      emphasis && "justify-center",
      // Deliberately left out of the hover treatment: animating a control that
      // does nothing promises an interaction that is not there.
      hint && "bg-ghost-white-60 border-primary-10 cursor-not-allowed"
    )}
  >
    {/* Same presentation as the logo column on the issuers page
        (views/issuers/components/IssuersTable). IssuerLogo renders nothing when
        src is empty, so an issuer without a logo degrades to name-only.
        Unreachable companies lose the logo's colour but keep their name at full
        contrast — greyed out must mean "not selectable", never "not
        readable". */}
    <IssuerLogo
      src={logoUrl ?? ""}
      name={name}
      className={cn(
        "ml-2 h-8 w-8 shrink-0 rounded-md shadow-sm",
        hint && "grayscale opacity-60"
      )}
    />

    <span
      className={cn(
        "pl-2 text-sm leading-tight text-left",
        emphasis && "pl-0 font-medium"
      )}
    >
      {name}
    </span>
  </button>
);
